import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The things you do to a whole conversation, rather than inside one.
 *
 * A menu rather than a row of icons because what goes in here is destructive
 * or slow — neither wants to be one stray tap away.
 *
 * The list is rendered into `document.body` rather than next to its button.
 * On a chat row the button lives inside the scrolling list, and a popover
 * drawn there is clipped by it: the first attempt at this menu lost its top
 * two items behind the edge of the list. Fixed positioning against the
 * button's measured rectangle is the only version that cannot be cropped —
 * and it flips above the button when there is no room below.
 */
export function ChatMenu({ items, glyph = 'dots', className = '' }: {
  items: Array<{ label: string; danger?: boolean; disabled?: boolean; onSelect: () => void }>;
  /** Three dots in a header, a chevron on a list row — WhatsApp's two shapes. */
  glyph?: 'dots' | 'chevron';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<{ right: number; top?: number; bottom?: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const place = useCallback(() => {
    const b = trigger.current?.getBoundingClientRect();
    if (!b) return;
    const right = Math.max(8, window.innerWidth - b.right);
    // Enough room below for the whole list, or it opens upward.
    const need = items.length * 38 + 16;
    setBox(b.bottom + need < window.innerHeight
      ? { right, top: b.bottom + 6 }
      : { right, bottom: window.innerHeight - b.top + 6 });
  }, [items.length]);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  // Anywhere else closes it, including the Escape key — a popover that only
  // closes by hitting the same small button again is a trap on a phone. A
  // scroll closes it too: fixed to the viewport, it would otherwise sit still
  // while the row it belongs to slides away underneath.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!trigger.current?.contains(t) && !list.current?.contains(t)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const gone = () => setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('resize', gone);
    document.addEventListener('scroll', gone, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', gone);
      document.removeEventListener('scroll', gone, true);
    };
  }, [open]);

  return (
    <div className={`chat-menu ${className}`} onClick={(e) => e.stopPropagation()}>
      <button ref={trigger} className="chat-menu-open" title="More" aria-label="More"
              aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {glyph === 'dots' ? (
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" />
            <circle cx="12" cy="19" r="1.7" />
          </svg>
        ) : (
          <svg className="chev" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M7 10l5 5 5-5" />
          </svg>
        )}
      </button>
      {open && box && createPortal(
        <ul className="chat-menu-list" ref={list} style={box}>
          {items.map((it) => (
            <li key={it.label}>
              <button className={it.danger ? 'danger' : ''} disabled={it.disabled}
                      onClick={() => { setOpen(false); it.onSelect(); }}>
                {it.label}
              </button>
            </li>
          ))}
        </ul>,
        document.body)}
    </div>
  );
}
